import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { LogPulseLogo } from '@/components/LogPulseLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSelector } from '@/components/LanguageSelector';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';

function LegalHeader() {
  const t = useT();
  return (
    <header className="sticky top-0 z-40 glass border-b border-gray-200/60 dark:border-gray-800/60">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          <Link to="/" className="flex items-center gap-2">
            <LogPulseLogo className="h-6 w-6" />
            <span className="font-bold text-gray-900 dark:text-white">LogPulse</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
            <a
              href="https://app.logpulse.io/sign-in"
              className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
            >
              {t('nav.getStarted')}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}

export function PrivacyPage() {
  const t = useT();

  useDocumentMeta({
    title: t('meta.privacy.title'),
    description: t('meta.privacy.description'),
  });

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <LegalHeader />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          {t('privacy.title')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-10">
          {t('privacy.lastUpdated')}
        </p>

        {/* Early Access Warning Banner */}
        <div className="mb-10 rounded-xl border-2 border-warning-500/30 bg-warning-50 dark:bg-warning-500/10 px-6 py-5">
          <h2 className="text-base font-bold text-warning-600 dark:text-warning-400 mb-2">
            {t('privacy.betaBanner.title')}
          </h2>
          <p className="text-sm text-warning-600/90 dark:text-warning-400/80 leading-relaxed">
            {t('privacy.betaBanner.description')}
          </p>
        </div>

        <div className="prose-legal space-y-8">
          {([
            'introduction',
            'dataWeCollect',
            'howWeUse',
            'dataStorage',
            'betaDataRisks',
            'dataSharing',
            'cookies',
            'yourRights',
            'dataRetention',
            'security',
            'children',
            'changes',
            'contact',
          ] as const).map((section) => (
            <section key={section}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {t(`privacy.${section}.title`)}
              </h2>
              <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-2 whitespace-pre-line">
                {t(`privacy.${section}.content`)}
              </div>
            </section>
          ))}
        </div>

        {/* Footer nav */}
        <div className="mt-16 pt-8 border-t border-gray-200 dark:border-gray-800 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
          <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">Home</Link>
          <Link to="/privacy" className="text-gray-900 dark:text-white font-medium">{t('footer.privacy')}</Link>
          <Link to="/terms" className="hover:text-gray-900 dark:hover:text-white transition-colors">{t('footer.terms')}</Link>
        </div>
      </main>
    </div>
  );
}
